import yaml from 'js-yaml'
import toml from 'toml'
import { XMLParser } from 'fast-xml-parser'
import type { DataFormat, DataNode, DataEdge, ParsedData, NodeType } from '@/types'

let nodeIdCounter = 0

function resetNodeCounter(): void {
  nodeIdCounter = 0
}

function generateNodeId(): string {
  return `node_${++nodeIdCounter}`
}

function getNodeType(value: unknown): NodeType {
  if (value === null || value === undefined) return 'null'
  if (Array.isArray(value)) return 'array'
  if (typeof value === 'object') return 'object'
  if (typeof value === 'string') return 'string'
  if (typeof value === 'number') return 'number'
  if (typeof value === 'boolean') return 'boolean'
  return 'string'
}

interface TraverseContext {
  nodes: DataNode[]
  edges: DataEdge[]
  maxDepth: number
  currentDepth: number
}

function traverseData(
  data: unknown,
  parentId: string | null,
  key: string,
  path: string,
  ctx: TraverseContext,
  depth: number = 0
): string {
  const nodeId = generateNodeId()
  const type = getNodeType(data)
  
  ctx.maxDepth = Math.max(ctx.maxDepth, depth)

  const node: DataNode = {
    id: nodeId,
    type: 'dataNode',
    position: { x: 0, y: 0 },
    data: {
      label: key,
      type,
      value: type !== 'object' && type !== 'array' ? data : undefined,
      path,
      collapsed: false,
      childCount: type === 'array' 
        ? (data as unknown[]).length 
        : type === 'object' 
          ? Object.keys(data as object).length 
          : 0,
    },
  }

  ctx.nodes.push(node)

  if (parentId) {
    ctx.edges.push({
      id: `edge_${parentId}_${nodeId}`,
      source: parentId,
      target: nodeId,
      type: 'smoothstep',
      data: { key },
    })
  }

  if (type === 'object' && data !== null) {
    Object.entries(data as Record<string, unknown>).forEach(([k, v]) => {
      traverseData(v, nodeId, k, `${path}.${k}`, ctx, depth + 1)
    })
  } else if (type === 'array') {
    (data as unknown[]).forEach((item, index) => {
      traverseData(item, nodeId, `[${index}]`, `${path}[${index}]`, ctx, depth + 1)
    })
  }

  return nodeId
}

function dataToGraph(data: unknown): { nodes: DataNode[]; edges: DataEdge[]; maxDepth: number } {
  resetNodeCounter()
  
  const ctx: TraverseContext = {
    nodes: [],
    edges: [],
    maxDepth: 0,
    currentDepth: 0,
  }

  traverseData(data, null, 'root', '$', ctx, 0)

  return {
    nodes: ctx.nodes,
    edges: ctx.edges,
    maxDepth: ctx.maxDepth,
  }
}

export function detectFormat(content: string): DataFormat {
  const trimmed = content.trim()
  const lines = trimmed.split(/\r?\n/)

  // EDI (EDIFACT or X12)
  if (trimmed.startsWith('UNA') || trimmed.startsWith('UNB+') ||
      trimmed.startsWith('ISA*') || /^ISA\*/.test(trimmed)) {
    return 'edi'
  }

  // OFX (check for OFX headers or tags)
  if (trimmed.includes('OFXHEADER:') ||
      trimmed.includes('<OFX>') ||
      /^OFXHEADER:/m.test(trimmed) ||
      (trimmed.startsWith('<?xml') && trimmed.includes('<OFX>'))) {
    return 'ofx'
  }

  // CNAB (fixed-width 240 or 400 characters per line)
  if (lines.length > 0) {
    const firstLine = lines[0].replace(/\r$/, '')
    // CNAB files have fixed line lengths and start with specific patterns
    if ((firstLine.length === 240 || firstLine.length === 400) &&
        /^\d{3}/.test(firstLine)) {
      // Check if it looks like a CNAB header (starts with bank code)
      const possibleBankCode = firstLine.substring(0, 3)
      if (/^\d{3}$/.test(possibleBankCode)) {
        return 'cnab'
      }
    }
  }

  // JSON
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      JSON.parse(trimmed)
      return 'json'
    } catch {
      // Not valid JSON
    }
  }

  // XML
  if (trimmed.startsWith('<?xml') || trimmed.startsWith('<')) {
    return 'xml'
  }

  // TOML (check for typical patterns)
  if (/^\[[\w.-]+\]/m.test(trimmed) || /^[\w.-]+\s*=/m.test(trimmed)) {
    try {
      toml.parse(trimmed)
      return 'toml'
    } catch {
      // Not valid TOML
    }
  }

  // Default to YAML (most permissive)
  return 'yaml'
}

export async function parseJSON(content: string): Promise<unknown> {
  return JSON.parse(content)
}

export async function parseYAML(content: string): Promise<unknown> {
  return yaml.load(content)
}

export async function parseXML(content: string): Promise<unknown> {
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    textNodeName: '#text',
    trimValues: true,
  })
  return parser.parse(content)
}

export async function parseTOML(content: string): Promise<unknown> {
  return toml.parse(content)
}

// EDI Parser - supports EDIFACT and X12 formats
export async function parseEDI(content: string): Promise<unknown> {

  // Detect EDI type and delimiters
  const isEDIFACT = content.includes('UNB+') || content.includes("UNA:")
  const isX12 = content.includes('ISA*') || /^ISA\*/.test(content.trim())

  let segmentDelimiter = "'"
  let elementDelimiter = '+'
  let componentDelimiter = ':'

  if (isX12) {
    segmentDelimiter = '~'
    elementDelimiter = '*'
    componentDelimiter = ':'
  } else if (isEDIFACT) {
    // Check for UNA segment which defines delimiters
    const unaMatch = content.match(/UNA(.)(.)(.)(.)(.)(.)/)
    if (unaMatch) {
      componentDelimiter = unaMatch[1]
      elementDelimiter = unaMatch[2]
      // unaMatch[3] is decimal notation
      // unaMatch[4] is release character
      // unaMatch[5] is reserved
      segmentDelimiter = unaMatch[6]
    }
  }

  // Split content into segments
  const rawSegments = content
    .replace(/\r?\n/g, '')
    .split(segmentDelimiter)
    .filter(s => s.trim())

  const result: Record<string, unknown> = {
    _type: isX12 ? 'X12' : isEDIFACT ? 'EDIFACT' : 'EDI',
    _segmentCount: rawSegments.length,
    segments: [] as unknown[]
  }

  const segments: unknown[] = []

  for (const rawSegment of rawSegments) {
    const elements = rawSegment.split(elementDelimiter)
    const segmentId = elements[0]?.trim()

    if (!segmentId) continue

    const segmentElements: Record<string, unknown> = {}

    // Parse elements (skip segment ID at index 0)
    for (let i = 1; i < elements.length; i++) {
      const element = elements[i]
      const elementKey = `${segmentId}${String(i).padStart(2, '0')}`

      // Check for components
      if (element.includes(componentDelimiter)) {
        const components = element.split(componentDelimiter)
        const componentObj: Record<string, string> = {}
        components.forEach((comp, idx) => {
          if (comp) {
            componentObj[`${elementKey}-${idx + 1}`] = comp
          }
        })
        segmentElements[elementKey] = componentObj
      } else {
        segmentElements[elementKey] = element || null
      }
    }

    const segment: Record<string, unknown> = {
      _id: segmentId,
      elements: segmentElements
    }

    segments.push(segment)
  }

  result.segments = segments

  // Group segments by type for better visualization
  const groupedSegments: Record<string, unknown[]> = {}
  for (const seg of segments) {
    const segObj = seg as Record<string, unknown>
    const id = segObj._id as string
    if (!groupedSegments[id]) {
      groupedSegments[id] = []
    }
    groupedSegments[id].push(segObj.elements)
  }

  result.grouped = groupedSegments

  return result
}

// OFX Parser - supports OFX 1.x (SGML) and OFX 2.x (XML)
export async function parseOFX(content: string): Promise<unknown> {
  const trimmed = content.trim()

  // Check if it's OFX 2.x (XML format)
  if (trimmed.startsWith('<?xml') || trimmed.startsWith('<?OFX')) {
    const parser = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: '@_',
      textNodeName: '#text',
      trimValues: true,
    })
    return parser.parse(content)
  }

  // OFX 1.x SGML format
  const result: Record<string, unknown> = {
    _type: 'OFX',
    _version: '1.x',
    headers: {} as Record<string, string>,
    data: {} as Record<string, unknown>
  }

  // Parse headers (before <OFX>)
  const headerMatch = content.match(/^([\s\S]*?)<OFX>/i)
  if (headerMatch) {
    const headerLines = headerMatch[1].split(/\r?\n/).filter(line => line.trim())
    for (const line of headerLines) {
      const [key, value] = line.split(':').map(s => s.trim())
      if (key && value !== undefined) {
        (result.headers as Record<string, string>)[key] = value
      }
    }
  }

  // Parse SGML-like structure
  function parseSGML(text: string): Record<string, unknown> {
    const obj: Record<string, unknown> = {}
    const stack: { name: string; obj: Record<string, unknown> }[] = [{ name: 'root', obj }]

    // Simple state machine for parsing
    const lines = text.split(/\r?\n/)
    for (const line of lines) {
      const trimmedLine = line.trim()
      if (!trimmedLine) continue

      // Opening tag with value: <TAG>value
      const valueMatch = trimmedLine.match(/^<(\w+)>(.+)$/)
      if (valueMatch) {
        const [, tag, value] = valueMatch
        const current = stack[stack.length - 1].obj
        current[tag] = value
        continue
      }

      // Opening tag without value: <TAG>
      const openMatch = trimmedLine.match(/^<(\w+)>$/)
      if (openMatch) {
        const [, tag] = openMatch
        const current = stack[stack.length - 1].obj
        const newObj: Record<string, unknown> = {}

        // Handle arrays (multiple same tags)
        if (current[tag]) {
          if (!Array.isArray(current[tag])) {
            current[tag] = [current[tag]]
          }
          (current[tag] as unknown[]).push(newObj)
        } else {
          current[tag] = newObj
        }

        stack.push({ name: tag, obj: newObj })
        continue
      }

      // Closing tag: </TAG>
      const closeMatch = trimmedLine.match(/^<\/(\w+)>$/)
      if (closeMatch) {
        const [, tag] = closeMatch
        if (stack.length > 1 && stack[stack.length - 1].name === tag) {
          stack.pop()
        }
        continue
      }
    }

    return obj
  }

  // Extract OFX content
  const ofxMatch = content.match(/<OFX>([\s\S]*?)<\/OFX>/i)
  if (ofxMatch) {
    result.data = parseSGML(ofxMatch[1])
  } else {
    // Try to parse everything after headers
    const ofxStart = content.indexOf('<OFX>')
    if (ofxStart !== -1) {
      result.data = parseSGML(content.substring(ofxStart))
    }
  }

  return result
}

// CNAB Parser - supports CNAB 240 and CNAB 400 formats
export async function parseCNAB(content: string): Promise<unknown> {
  const lines = content.split(/\r?\n/).filter(line => line.trim())

  if (lines.length === 0) {
    throw new Error('Empty CNAB file')
  }

  // Detect format based on line length
  const firstLineLength = lines[0].replace(/\r$/, '').length
  const isCNAB240 = firstLineLength === 240
  const isCNAB400 = firstLineLength === 400

  if (!isCNAB240 && !isCNAB400) {
    throw new Error(`Invalid CNAB format: line length is ${firstLineLength}, expected 240 or 400`)
  }

  const result: Record<string, unknown> = {
    _type: isCNAB240 ? 'CNAB240' : 'CNAB400',
    _lineCount: lines.length,
    header: {} as Record<string, unknown>,
    lotes: [] as unknown[],
    trailer: {} as Record<string, unknown>
  }

  if (isCNAB240) {
    return parseCNAB240(lines, result)
  } else {
    return parseCNAB400(lines, result)
  }
}

function parseCNAB240(lines: string[], result: Record<string, unknown>): Record<string, unknown> {
  // CNAB 240 structure
  // Header de Arquivo (tipo 0)
  // Header de Lote (tipo 1)
  // Registros Detalhe (tipo 3)
  // Trailer de Lote (tipo 5)
  // Trailer de Arquivo (tipo 9)

  let currentLote: Record<string, unknown> | null = null
  const lotes: unknown[] = []

  for (const line of lines) {
    const tipoRegistro = line.charAt(7) // Position 8 (0-indexed: 7)

    switch (tipoRegistro) {
      case '0': // Header de Arquivo
        result.header = {
          tipoRegistro: '0 - Header de Arquivo',
          codigoBanco: line.substring(0, 3),
          loteServico: line.substring(3, 7),
          tipoInscricao: line.charAt(17),
          numeroInscricao: line.substring(18, 32).trim(),
          convenio: line.substring(32, 52).trim(),
          agencia: line.substring(52, 57).trim(),
          digitoAgencia: line.charAt(57),
          conta: line.substring(58, 70).trim(),
          digitoConta: line.charAt(70),
          nomeEmpresa: line.substring(72, 102).trim(),
          nomeBanco: line.substring(102, 132).trim(),
          dataGeracao: formatDate(line.substring(143, 151)),
          horaGeracao: formatTime(line.substring(151, 157)),
          numeroSequencial: line.substring(157, 163).trim(),
          versaoLayout: line.substring(163, 166),
        }
        break

      case '1': // Header de Lote
        currentLote = {
          tipoRegistro: '1 - Header de Lote',
          codigoBanco: line.substring(0, 3),
          loteServico: line.substring(3, 7),
          tipoOperacao: line.charAt(8),
          tipoServico: line.substring(9, 11),
          formaLancamento: line.substring(11, 13),
          versaoLayout: line.substring(13, 16),
          tipoInscricao: line.charAt(17),
          numeroInscricao: line.substring(18, 33).trim(),
          convenio: line.substring(33, 53).trim(),
          agencia: line.substring(53, 58).trim(),
          conta: line.substring(58, 70).trim(),
          nomeEmpresa: line.substring(72, 102).trim(),
          detalhes: [] as unknown[]
        }
        break

      case '3': // Registro Detalhe
        if (currentLote) {
          const segmento = line.charAt(13)
          const detalhe: Record<string, unknown> = {
            tipoRegistro: '3 - Detalhe',
            segmento,
            codigoBanco: line.substring(0, 3),
            loteServico: line.substring(3, 7),
            numeroRegistro: line.substring(8, 13),
          }

          // Parse based on segment type
          if (segmento === 'A') {
            Object.assign(detalhe, {
              tipoMovimento: line.substring(14, 17),
              camaraCompensacao: line.substring(17, 20),
              bancoFavorecido: line.substring(20, 23),
              agenciaFavorecido: line.substring(23, 28).trim(),
              contaFavorecido: line.substring(28, 40).trim(),
              nomeFavorecido: line.substring(43, 73).trim(),
              seuNumero: line.substring(73, 93).trim(),
              dataVencimento: formatDate(line.substring(93, 101)),
              valorTitulo: formatCurrency(line.substring(119, 134)),
            })
          } else if (segmento === 'B') {
            Object.assign(detalhe, {
              tipoInscricao: line.charAt(17),
              numeroInscricao: line.substring(18, 32).trim(),
              logradouro: line.substring(32, 62).trim(),
              numero: line.substring(62, 67).trim(),
              complemento: line.substring(67, 82).trim(),
              bairro: line.substring(82, 97).trim(),
              cidade: line.substring(97, 117).trim(),
              cep: line.substring(117, 125),
              estado: line.substring(125, 127),
            })
          } else if (segmento === 'J') {
            Object.assign(detalhe, {
              codigoBarras: line.substring(17, 61).trim(),
              nomeCedente: line.substring(61, 91).trim(),
              dataVencimento: formatDate(line.substring(91, 99)),
              valorTitulo: formatCurrency(line.substring(99, 114)),
              desconto: formatCurrency(line.substring(114, 129)),
              acrescimo: formatCurrency(line.substring(129, 144)),
              dataPagamento: formatDate(line.substring(144, 152)),
              valorPagamento: formatCurrency(line.substring(152, 167)),
            })
          }

          (currentLote.detalhes as unknown[]).push(detalhe)
        }
        break

      case '5': // Trailer de Lote
        if (currentLote) {
          currentLote.trailer = {
            tipoRegistro: '5 - Trailer de Lote',
            codigoBanco: line.substring(0, 3),
            loteServico: line.substring(3, 7),
            quantidadeRegistros: parseInt(line.substring(17, 23), 10),
            valorTotal: formatCurrency(line.substring(23, 41)),
          }
          lotes.push(currentLote)
          currentLote = null
        }
        break

      case '9': // Trailer de Arquivo
        result.trailer = {
          tipoRegistro: '9 - Trailer de Arquivo',
          codigoBanco: line.substring(0, 3),
          loteServico: line.substring(3, 7),
          quantidadeLotes: parseInt(line.substring(17, 23), 10),
          quantidadeRegistros: parseInt(line.substring(23, 29), 10),
        }
        break
    }
  }

  result.lotes = lotes
  return result
}

function parseCNAB400(lines: string[], result: Record<string, unknown>): Record<string, unknown> {
  // CNAB 400 structure
  // Header (tipo 0)
  // Transações (tipo 1 ou 7)
  // Trailer (tipo 9)

  const transacoes: unknown[] = []

  for (const line of lines) {
    const tipoRegistro = line.charAt(0)

    switch (tipoRegistro) {
      case '0': // Header
        result.header = {
          tipoRegistro: '0 - Header',
          codigoRemessa: line.charAt(1),
          literalRemessa: line.substring(2, 9).trim(),
          codigoServico: line.substring(9, 11),
          literalServico: line.substring(11, 26).trim(),
          codigoEmpresa: line.substring(26, 46).trim(),
          nomeEmpresa: line.substring(46, 76).trim(),
          codigoBanco: line.substring(76, 79),
          nomeBanco: line.substring(79, 94).trim(),
          dataGravacao: formatDate(line.substring(94, 100)),
          densidadeGravacao: line.substring(100, 105).trim(),
          numeroSequencial: line.substring(394, 400).trim(),
        }
        break

      case '1': // Transação (Cobrança)
      case '7': // Transação (Pagamento)
        const transacao: Record<string, unknown> = {
          tipoRegistro: tipoRegistro === '1' ? '1 - Transação' : '7 - Transação',
          tipoInscricao: line.substring(1, 3),
          numeroInscricao: line.substring(3, 17).trim(),
          agencia: line.substring(17, 21).trim(),
          digitoAgencia: line.substring(21, 23).trim(),
          conta: line.substring(23, 30).trim(),
          digitoConta: line.charAt(30),
          seuNumero: line.substring(37, 62).trim(),
          nossoNumero: line.substring(62, 73).trim(),
          dataVencimento: formatDate(line.substring(120, 126)),
          valorTitulo: formatCurrency(line.substring(126, 139)),
          especieTitulo: line.substring(148, 150),
          dataEmissao: formatDate(line.substring(150, 156)),
          valorDesconto: formatCurrency(line.substring(179, 192)),
          valorIOF: formatCurrency(line.substring(192, 205)),
          valorAbatimento: formatCurrency(line.substring(205, 218)),
          nomePagador: line.substring(234, 274).trim(),
          enderecoPagador: line.substring(274, 314).trim(),
          cepPagador: line.substring(326, 334).trim(),
          numeroSequencial: line.substring(394, 400).trim(),
        }
        transacoes.push(transacao)
        break

      case '9': // Trailer
        result.trailer = {
          tipoRegistro: '9 - Trailer',
          quantidadeRegistros: lines.length,
          numeroSequencial: line.substring(394, 400).trim(),
        }
        break
    }
  }

  result.transacoes = transacoes
  return result
}

// Helper functions for CNAB
function formatDate(dateStr: string): string {
  if (!dateStr || dateStr.trim() === '' || dateStr === '00000000' || dateStr === '000000') {
    return ''
  }

  // DDMMYYYY or DDMMYY format
  if (dateStr.length === 8) {
    return `${dateStr.substring(0, 2)}/${dateStr.substring(2, 4)}/${dateStr.substring(4, 8)}`
  } else if (dateStr.length === 6) {
    const year = parseInt(dateStr.substring(4, 6), 10)
    const fullYear = year > 50 ? 1900 + year : 2000 + year
    return `${dateStr.substring(0, 2)}/${dateStr.substring(2, 4)}/${fullYear}`
  }
  return dateStr
}

function formatTime(timeStr: string): string {
  if (!timeStr || timeStr.trim() === '' || timeStr === '000000') {
    return ''
  }
  return `${timeStr.substring(0, 2)}:${timeStr.substring(2, 4)}:${timeStr.substring(4, 6)}`
}

function formatCurrency(valueStr: string): string {
  if (!valueStr || valueStr.trim() === '') {
    return '0,00'
  }
  const value = parseInt(valueStr.replace(/\D/g, ''), 10) / 100
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export async function parseContent(content: string, format: DataFormat = 'auto'): Promise<ParsedData> {
  const startTime = performance.now()
  
  const detectedFormat = format === 'auto' ? detectFormat(content) : format
  
  let data: unknown

  switch (detectedFormat) {
    case 'json':
      data = await parseJSON(content)
      break
    case 'yaml':
      data = await parseYAML(content)
      break
    case 'xml':
      data = await parseXML(content)
      break
    case 'toml':
      data = await parseTOML(content)
      break
    case 'edi':
      data = await parseEDI(content)
      break
    case 'ofx':
      data = await parseOFX(content)
      break
    case 'cnab':
      data = await parseCNAB(content)
      break
    default:
      throw new Error(`Unsupported format: ${format}`)
  }

  const { nodes, edges, maxDepth } = dataToGraph(data)
  const parseTime = performance.now() - startTime

  return {
    nodes,
    edges,
    metadata: {
      format: detectedFormat,
      nodeCount: nodes.length,
      maxDepth,
      parseTime,
    },
  }
}

export function getValueDisplay(node: DataNode): string {
  const { type, value, childCount } = node.data

  if (type === 'object') return `{${childCount}}`
  if (type === 'array') return `[${childCount}]`
  if (type === 'null') return 'null'
  if (type === 'string') {
    const str = String(value)
    return str.length > 30 ? `"${str.substring(0, 30)}..."` : `"${str}"`
  }
  return String(value)
}

// Parse a JSONPath and set a value in an object
export function setValueByPath(obj: unknown, path: string, value: unknown): unknown {
  // Clone the object to avoid mutations
  const result = JSON.parse(JSON.stringify(obj))

  // Parse path like $.foo.bar[0].baz
  const segments = path
    .replace(/^\$\.?/, '') // Remove leading $
    .split(/\.|\[|\]/)
    .filter(Boolean)

  if (segments.length === 0) return value

  let current: Record<string, unknown> | unknown[] = result
  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i]
    const index = parseInt(segment, 10)
    if (!isNaN(index)) {
      current = (current as unknown[])[index] as Record<string, unknown> | unknown[]
    } else {
      current = (current as Record<string, unknown>)[segment] as Record<string, unknown> | unknown[]
    }
  }

  const lastSegment = segments[segments.length - 1]
  const lastIndex = parseInt(lastSegment, 10)
  if (!isNaN(lastIndex)) {
    (current as unknown[])[lastIndex] = value
  } else {
    (current as Record<string, unknown>)[lastSegment] = value
  }

  return result
}

// Serialize data back to the original format
export function serializeContent(data: unknown, format: DataFormat): string {
  switch (format) {
    case 'json':
      return JSON.stringify(data, null, 2)
    case 'yaml':
      return yaml.dump(data, { indent: 2, lineWidth: -1 })
    case 'toml':
      // toml library doesn't have stringify, convert to JSON as fallback
      return JSON.stringify(data, null, 2)
    case 'xml':
      // xml2js doesn't have a simple stringify, convert to JSON as fallback
      return JSON.stringify(data, null, 2)
    default:
      return JSON.stringify(data, null, 2)
  }
}
